import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { api, getApiErrorMessage } from '../lib/api'
import { useToast } from '../ui/ToastProvider'

function unwrap(res) {
  return res?.data?.data !== undefined ? res.data.data : res.data
}

const emptyTemplateForm = {
  id: null,
  templateCode: '',
  name: '',
  description: '',
  ageBandLabel: '',
  minAge: '',
  maxAge: '',
  active: true,
  defaultTemplate: false,
  displayOrder: 0,
}

function amountToManWon(value) {
  const n = Number(value || 0)
  if (!Number.isFinite(n)) return ''
  if (n <= 1) return String(Math.max(0, n))
  return String(Math.round(n / 10000))
}

function manWonToWon(value, originalValue) {
  const n = Number(String(value ?? '').replace(/,/g, ''))
  if (!Number.isFinite(n) || n < 0) return 0
  if (Number(originalValue) <= 1 && n <= 1) return Math.round(n)
  return Math.round(n * 10000)
}

function normalizeItem(row) {
  return {
    ...row,
    minAmountManWon: row?.minAmountManWon ?? amountToManWon(row?.minAmount),
    targetAmountManWon: row?.targetAmountManWon ?? amountToManWon(row?.targetAmount),
    maxAmountManWon: row?.maxAmountManWon ?? amountToManWon(row?.maxAmount),
  }
}

function templateFormFromRow(row) {
  if (!row) return emptyTemplateForm
  return {
    id: row.id || null,
    templateCode: row.templateCode || '',
    name: row.name || '',
    description: row.description || '',
    ageBandLabel: row.ageBandLabel || '',
    minAge: row.minAge ?? '',
    maxAge: row.maxAge ?? '',
    active: row.active !== false,
    defaultTemplate: row.defaultTemplate === true,
    displayOrder: Number(row.displayOrder || 0),
  }
}

function templatePayload(form) {
  const numberOrNull = (value) => {
    if (value === '' || value === null || value === undefined) return null
    const n = Number(value)
    return Number.isFinite(n) ? Math.round(n) : null
  }
  return {
    templateCode: form.templateCode || null,
    name: form.name || '',
    description: form.description || '',
    ageBandLabel: form.ageBandLabel || '',
    minAge: numberOrNull(form.minAge),
    maxAge: numberOrNull(form.maxAge),
    active: form.active !== false,
    defaultTemplate: form.defaultTemplate === true,
    displayOrder: Number(form.displayOrder || 0),
  }
}

function itemPayload(row) {
  return {
    category: row.category || '',
    label: row.label || '',
    importance: row.importance || '권장',
    minAmount: manWonToWon(row.minAmountManWon, row.minAmount),
    targetAmount: manWonToWon(row.targetAmountManWon, row.targetAmount),
    maxAmount: manWonToWon(row.maxAmountManWon, row.maxAmount),
    weight: Number(row.weight || 5),
    ageAdjustmentPercent: Number(row.ageAdjustmentPercent ?? 10),
    keywords: row.keywords || '',
    displayOrder: Number(row.displayOrder || 0),
    active: row.active !== false,
    note: row.note || '',
  }
}

function ageRangeLabel(row) {
  if (row?.ageBandLabel) return row.ageBandLabel
  if (row?.minAge != null && row?.maxAge != null) return `${row.minAge}~${row.maxAge}세`
  if (row?.minAge != null) return `${row.minAge}세 이상`
  if (row?.maxAge != null) return `${row.maxAge}세 이하`
  return '전체'
}

export default function CoverageRuleTemplatesPage() {
  const { show } = useToast()
  const [templates, setTemplates] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [form, setForm] = useState(emptyTemplateForm)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [savingRuleCode, setSavingRuleCode] = useState('')
  const [error, setError] = useState('')

  const selectedTemplate = useMemo(
    () => templates.find((row) => String(row.id) === String(selectedId)) || null,
    [templates, selectedId],
  )

  const loadTemplates = useCallback(async (nextSelectedId) => {
    setLoading(true)
    setError('')
    try {
      const rows = unwrap(await api.get('/customer-management/integrated-report/coverage-rule-templates', { __silent: true }))
      const list = Array.isArray(rows) ? rows : []
      setTemplates(list)
      const targetId = nextSelectedId || selectedId || list[0]?.id || null
      if (targetId) {
        setSelectedId(targetId)
        return targetId
      }
      setForm(emptyTemplateForm)
      setItems([])
      return null
    } catch (e) {
      setError(getApiErrorMessage(e, '표준가입금액 템플릿을 불러오지 못했습니다.'))
      setTemplates([])
      return null
    } finally {
      setLoading(false)
    }
  }, [selectedId])

  const loadDetail = useCallback(async (templateId) => {
    if (!templateId) return
    setDetailLoading(true)
    setError('')
    try {
      const detail = unwrap(await api.get(`/customer-management/integrated-report/coverage-rule-templates/${templateId}`, { __silent: true }))
      setForm(templateFormFromRow(detail))
      setItems(Array.isArray(detail?.items) ? detail.items.map(normalizeItem) : [])
    } catch (e) {
      setError(getApiErrorMessage(e, '표준가입금액 템플릿 상세를 불러오지 못했습니다.'))
      setItems([])
    } finally {
      setDetailLoading(false)
    }
  }, [])

  useEffect(() => {
    loadTemplates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (selectedId) loadDetail(selectedId)
  }, [selectedId, loadDetail])

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const startNew = () => {
    setSelectedId(null)
    setForm({
      ...emptyTemplateForm,
      templateCode: `RULE_TEMPLATE_${Date.now()}`,
      name: '새 표준가입금액 템플릿',
      active: true,
      displayOrder: (templates.length + 1) * 10,
    })
    setItems([])
  }

  const saveTemplate = async () => {
    setSavingTemplate(true)
    setError('')
    try {
      const payload = templatePayload(form)
      const saved = form.id
        ? unwrap(await api.patch(`/customer-management/integrated-report/coverage-rule-templates/${form.id}`, payload))
        : unwrap(await api.post('/customer-management/integrated-report/coverage-rule-templates', payload))
      show('표준가입금액 템플릿을 저장했습니다.', 'success')
      const targetId = saved?.id || form.id
      await loadTemplates(targetId)
      if (targetId) await loadDetail(targetId)
    } catch (e) {
      setError(getApiErrorMessage(e, '템플릿을 저장하지 못했습니다.'))
    } finally {
      setSavingTemplate(false)
    }
  }

  const resetItems = async () => {
    if (!form.id) return
    if (!window.confirm('현재 템플릿의 담보 기준을 현재 기본 표준가입금액 기준으로 다시 구성할까요?')) return
    setDetailLoading(true)
    setError('')
    try {
      const detail = unwrap(await api.post(`/customer-management/integrated-report/coverage-rule-templates/${form.id}/reset-items`))
      setItems(Array.isArray(detail?.items) ? detail.items.map(normalizeItem) : [])
      show('템플릿 담보 기준을 다시 구성했습니다.', 'success')
    } catch (e) {
      setError(getApiErrorMessage(e, '템플릿 담보 기준을 다시 구성하지 못했습니다.'))
    } finally {
      setDetailLoading(false)
    }
  }

  const updateItem = (ruleCode, key, value) => {
    setItems((rows) => rows.map((row) => (
      row.ruleCode === ruleCode ? { ...row, [key]: value } : row
    )))
  }

  const saveItem = async (row) => {
    if (!form.id || !row?.ruleCode) return
    setSavingRuleCode(row.ruleCode)
    setError('')
    try {
      const saved = unwrap(await api.patch(
        `/customer-management/integrated-report/coverage-rule-templates/${form.id}/items/${encodeURIComponent(row.ruleCode)}`,
        itemPayload(row),
      ))
      setItems((rows) => rows.map((item) => item.ruleCode === row.ruleCode ? normalizeItem(saved) : item))
      show('담보 기준을 저장했습니다.', 'success')
    } catch (e) {
      setError(getApiErrorMessage(e, '담보 기준을 저장하지 못했습니다.'))
    } finally {
      setSavingRuleCode('')
    }
  }

  return (
    <div className="w-full max-w-none space-y-5 px-4 py-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">표준가입금액 설정</h1>
          <p className="mt-1 text-sm text-slate-500">
            연령대별 통합 리포트 권장 가입금액 기준을 관리합니다.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            onClick={() => loadTemplates(selectedId)}
            disabled={loading}
          >
            새로고침
          </button>
          <button
            type="button"
            className="rounded bg-brand-navy px-3 py-2 text-sm font-bold text-white hover:bg-brand-navy/90"
            onClick={startNew}
          >
            새 템플릿
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">템플릿 목록</div>
          {loading ? (
            <div className="rounded border border-slate-200 bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">불러오는 중…</div>
          ) : (
            <div className="space-y-2">
              {templates.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  className={`w-full rounded border px-3 py-2 text-left transition-colors ${String(row.id) === String(selectedId)
                    ? 'border-brand-navy bg-brand-navy/5'
                    : 'border-slate-200 bg-white hover:bg-slate-50'
                  }`}
                  onClick={() => setSelectedId(row.id)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-bold text-slate-900">{row.name}</span>
                    <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                      {ageRangeLabel(row)}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1 text-[10px] font-semibold">
                    {row.defaultTemplate ? <span className="rounded bg-blue-50 px-1.5 py-0.5 text-blue-700">기본</span> : null}
                    <span className={`rounded px-1.5 py-0.5 ${row.active === false ? 'bg-slate-100 text-slate-500' : 'bg-emerald-50 text-emerald-700'}`}>
                      {row.active === false ? '미사용' : '사용'}
                    </span>
                    <span className="rounded bg-slate-50 px-1.5 py-0.5 text-slate-500">{row.itemCount || 0}개 담보</span>
                  </div>
                </button>
              ))}
              {templates.length === 0 ? (
                <div className="rounded border border-dashed border-slate-200 px-3 py-6 text-center text-sm text-slate-500">
                  등록된 템플릿이 없습니다.
                </div>
              ) : null}
            </div>
          )}
        </aside>

        <main className="space-y-4">
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-bold text-slate-900">템플릿 기본정보</div>
                {selectedTemplate ? (
                  <div className="mt-0.5 text-xs text-slate-500">{selectedTemplate.description || ageRangeLabel(selectedTemplate)}</div>
                ) : null}
              </div>
              <button
                type="button"
                className="rounded bg-brand-navy px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                onClick={saveTemplate}
                disabled={savingTemplate}
              >
                {savingTemplate ? '저장 중…' : '템플릿 저장'}
              </button>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-4">
              <label className="text-xs font-semibold text-slate-700">
                템플릿명
                <input
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={form.name}
                  onChange={(e) => updateForm('name', e.target.value)}
                />
              </label>
              <label className="text-xs font-semibold text-slate-700">
                표시 연령대
                <input
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={form.ageBandLabel}
                  onChange={(e) => updateForm('ageBandLabel', e.target.value)}
                  placeholder="예: 30대"
                />
              </label>
              <label className="text-xs font-semibold text-slate-700">
                시작 나이
                <input
                  type="number"
                  min="0"
                  max="129"
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={form.minAge}
                  onChange={(e) => updateForm('minAge', e.target.value)}
                />
              </label>
              <label className="text-xs font-semibold text-slate-700">
                종료 나이
                <input
                  type="number"
                  min="0"
                  max="129"
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={form.maxAge}
                  onChange={(e) => updateForm('maxAge', e.target.value)}
                />
              </label>
              <label className="text-xs font-semibold text-slate-700 md:col-span-2">
                설명
                <input
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={form.description}
                  onChange={(e) => updateForm('description', e.target.value)}
                />
              </label>
              <label className="text-xs font-semibold text-slate-700">
                정렬
                <input
                  type="number"
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={form.displayOrder}
                  onChange={(e) => updateForm('displayOrder', e.target.value)}
                />
              </label>
              <div className="flex items-end gap-4 text-xs font-semibold text-slate-700">
                <label className="inline-flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={form.active !== false}
                    onChange={(e) => updateForm('active', e.target.checked)}
                  />
                  사용
                </label>
                <label className="inline-flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={form.defaultTemplate === true}
                    onChange={(e) => updateForm('defaultTemplate', e.target.checked)}
                  />
                  기본
                </label>
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
              <div>
                <div className="text-sm font-bold text-slate-900">담보별 기준금액</div>
                <div className="mt-0.5 text-xs text-slate-500">금액은 만원 단위로 입력합니다.</div>
              </div>
              <button
                type="button"
                className="rounded border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-900 disabled:opacity-50"
                onClick={resetItems}
                disabled={!form.id || detailLoading}
              >
                기본 룰로 다시 구성
              </button>
            </div>

            {detailLoading ? (
              <div className="px-4 py-10 text-center text-sm text-slate-500">불러오는 중…</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-[1120px] w-full border-collapse text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="border-b px-3 py-2">사용</th>
                      <th className="border-b px-3 py-2">분류/담보</th>
                      <th className="border-b px-3 py-2">중요도</th>
                      <th className="border-b px-3 py-2">최소</th>
                      <th className="border-b px-3 py-2">권장</th>
                      <th className="border-b px-3 py-2">상한</th>
                      <th className="border-b px-3 py-2">가중치</th>
                      <th className="border-b px-3 py-2">연령보정</th>
                      <th className="border-b px-3 py-2">키워드</th>
                      <th className="border-b px-3 py-2">저장</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((row) => (
                      <tr key={row.ruleCode} className="align-top">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={row.active !== false}
                            onChange={(e) => updateItem(row.ruleCode, 'active', e.target.checked)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            className="mb-1 w-28 rounded border border-slate-300 px-2 py-1"
                            value={row.category || ''}
                            onChange={(e) => updateItem(row.ruleCode, 'category', e.target.value)}
                          />
                          <input
                            className="w-44 rounded border border-slate-300 px-2 py-1 font-semibold text-slate-900"
                            value={row.label || ''}
                            onChange={(e) => updateItem(row.ruleCode, 'label', e.target.value)}
                          />
                          <div className="mt-1 text-[10px] text-slate-400">{row.ruleCode}</div>
                        </td>
                        <td className="px-3 py-2">
                          <select
                            className="w-20 rounded border border-slate-300 px-2 py-1"
                            value={row.importance || '권장'}
                            onChange={(e) => updateItem(row.ruleCode, 'importance', e.target.value)}
                          >
                            <option value="필수">필수</option>
                            <option value="권장">권장</option>
                            <option value="선택">선택</option>
                          </select>
                        </td>
                        {['minAmountManWon', 'targetAmountManWon', 'maxAmountManWon'].map((key) => (
                          <td key={key} className="px-3 py-2">
                            <input
                              type="number"
                              min="0"
                              className="w-24 rounded border border-slate-300 px-2 py-1 text-right"
                              value={row[key] ?? ''}
                              onChange={(e) => updateItem(row.ruleCode, key, e.target.value)}
                            />
                            <div className="mt-1 text-right text-[10px] text-slate-400">만원</div>
                          </td>
                        ))}
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="1"
                            max="20"
                            className="w-16 rounded border border-slate-300 px-2 py-1 text-right"
                            value={row.weight ?? 5}
                            onChange={(e) => updateItem(row.ruleCode, 'weight', e.target.value)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            max="50"
                            className="w-20 rounded border border-slate-300 px-2 py-1 text-right"
                            value={row.ageAdjustmentPercent ?? 10}
                            onChange={(e) => updateItem(row.ruleCode, 'ageAdjustmentPercent', e.target.value)}
                          />
                          <div className="mt-1 text-right text-[10px] text-slate-400">%</div>
                        </td>
                        <td className="px-3 py-2">
                          <textarea
                            className="min-h-[56px] w-64 rounded border border-slate-300 px-2 py-1"
                            value={row.keywords || ''}
                            onChange={(e) => updateItem(row.ruleCode, 'keywords', e.target.value)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            className="rounded bg-brand-navy px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                            disabled={!form.id || savingRuleCode === row.ruleCode}
                            onClick={() => saveItem(row)}
                          >
                            {savingRuleCode === row.ruleCode ? '저장 중' : '저장'}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-4 py-8 text-center text-sm text-slate-500">
                          템플릿을 저장하면 현재 기본 룰을 기준으로 담보 항목이 구성됩니다.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  )
}
